import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TrendingUp, TrendingDown, ArrowUp, ArrowDown, Minus, Info, Calendar, BarChart2, Inbox, Zap, Percent } from 'lucide-react';

// Info tooltip component
const InfoTooltip: React.FC<{ text: string }> = ({ text }) => {
  const [show, setShow] = useState(false);
  return (
    <div className="relative inline-block ml-1">
      <Info
        className="h-4 w-4 text-t4 hover:text-blue-600 dark:hover:text-blue-400 cursor-help inline"
        onMouseEnter={() => setShow(true)}
        onMouseLeave={() => setShow(false)}
        onClick={() => setShow(!show)}
      />
      {show && (
        <div className="absolute z-50 w-64 p-3 bg-surface-0 text-t1 text-xs rounded-lg shadow-lg border border-line -top-2 left-6">
          {text}
          <div className="absolute left-0 top-3 -ml-2 border-8 border-transparent border-r-slate-900" />
        </div>
      )}
    </div>
  );
};

interface FinancialAnalyticsProps {
  data: any;
}

const FinancialAnalytics: React.FC<FinancialAnalyticsProps> = ({ data }) => {
  const { t } = useTranslation(['analytics', 'common']);

  if (!data) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('el-GR', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount || 0);
  };

  const formatPercentage = (value: number) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getTrendIcon = (trend: 'up' | 'down' | 'stable') => { // eslint-disable-line @typescript-eslint/no-unused-vars
    switch (trend) {
      case 'up': return <TrendingUp className="h-5 w-5 text-green-500" />;
      case 'down': return <TrendingDown className="h-5 w-5 text-red-500" />;
      default: return <Minus className="h-5 w-5 text-t4" />;
    }
  };

  // Μικτά = «Σύνολο εισπράξεων» του Ζ· η ανάλυση μόνο όταν υπάρχουν τραπέζια/προκαταβολές (αλλιώς = οι παραγγελίες).
  const parts = data.gross_breakdown;
  const showParts = !!parts && (Math.abs(parts.dine_in || 0) > 0.004 || Math.abs(parts.deposits || 0) > 0.004);
  const vatRows: Array<{ rate: number | null; net: number; vat: number; gross: number }> = Array.isArray(data.vat_breakdown) ? data.vat_breakdown : [];
  const vatTotal = vatRows.reduce((s, v) => ({ net: s.net + (v.net || 0), vat: s.vat + (v.vat || 0), gross: s.gross + (v.gross || 0) }), { net: 0, vat: 0, gross: 0 });
  const diff = Number(data.fiscal_collections_diff) || 0;
  const signed = (v: number) => `${v > 0 ? '+' : v < 0 ? '−' : ''}${formatCurrency(Math.abs(v))}`;

  return (
    <div className="space-y-6">
      {/* Μόνο ό,τι μετριέται: μικτά = «Σύνολο εισπράξεων» του Ζ (παραγγελίες + τραπέζια + προκαταβολές που
          κρατήθηκαν), καθαρά/ΦΠΑ από τα παραστατικά. Κόστη/κέρδος/περιθώρια αφαιρέθηκαν (ήταν σταθερές
          εκτιμήσεις, όχι δεδομένα). */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
        <div className="bg-gradient-to-br from-blue-500/10 to-indigo-500/5 rounded-2xl border border-blue-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center mb-1.5">
            {t('analytics:financials.grossRevenue')}
            <InfoTooltip text={t('analytics:financials.grossRevenueTooltip')} />
          </p>
          <div className="text-xl sm:text-2xl font-bold text-t1">{formatCurrency(data.gross_revenue)}</div>
          <div className="flex items-center mt-1.5 text-xs">
            {data.revenue_trend === 'up' ? (
              <ArrowUp className="h-3 w-3 text-green-600 dark:text-green-400 mr-0.5" />
            ) : data.revenue_trend === 'down' ? (
              <ArrowDown className="h-3 w-3 text-red-600 dark:text-red-400 mr-0.5" />
            ) : (
              <Minus className="h-3 w-3 text-t4 mr-0.5" />
            )}
            <span className={
              data.revenue_trend === 'up' ? 'text-green-600 dark:text-green-400' :
              data.revenue_trend === 'down' ? 'text-red-600 dark:text-red-400' :
              'text-t3'
            }>
              {formatPercentage(Math.abs(data.revenue_change_percentage))}
            </span>
          </div>
          {showParts && (
            <div className="text-xs text-t4 mt-1" data-testid="gross-breakdown">
              {t('analytics:financials.grossBreakdown', { orders: formatCurrency(parts.orders), tables: formatCurrency(parts.dine_in) })}
              {Math.abs(parts.deposits || 0) > 0.004 && ` · ${t('analytics:financials.grossDeposits', { amount: formatCurrency(parts.deposits) })}`}
            </div>
          )}
        </div>

        <div className="bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 rounded-2xl border border-cyan-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center mb-1.5">
            {t('analytics:financials.netRevenue')}
            <InfoTooltip text={t('analytics:financials.netRevenueTooltip')} />
          </p>
          <div className="text-xl sm:text-2xl font-bold text-cyan-600 dark:text-cyan-400">{formatCurrency(data.net_revenue)}</div>
          <div className="text-xs text-t4 mt-1">
            {t('analytics:financials.afterVatDiscounts')}
          </div>
        </div>

        <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-2xl border border-amber-500/20 p-4 sm:p-5">
          <p className="text-xs sm:text-sm font-medium text-t3 flex items-center mb-1.5">
            {t('analytics:financials.vatTile')}
            <InfoTooltip text={t('analytics:financials.vatTileTooltip')} />
          </p>
          <div className="text-xl sm:text-2xl font-bold text-amber-700 dark:text-amber-400">{formatCurrency(data.vat_amount)}</div>
          <div className={`text-xs mt-1 ${data.vat_from_documents === false ? 'text-amber-700/80 dark:text-amber-400/80' : 'text-t4'}`}>
            {data.vat_from_documents === false
              ? t('analytics:financials.vatEstimate')
              : t('analytics:financials.vatFromDocuments')}
          </div>
        </div>
      </div>

      {/* ΦΠΑ ανά συντελεστή από τα παραστατικά (παραγγελίες ΚΑΙ τραπέζια) + «Παραστατικά − εισπράξεις», όπως το Ζ. */}
      {vatRows.length > 0 && (
        <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden" data-testid="vat-breakdown">
          <div className="p-5 border-b border-line">
            <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
              <Percent className="w-5 h-5" />
              {t('analytics:financials.vatByRate')}
            </h3>
            <p className="text-sm text-t3 mt-1">{t('analytics:financials.vatByRateHint')}</p>
          </div>
          <div className="p-5">
            <div className="overflow-x-auto">
              <table className="w-full" data-testid="vat-breakdown-table">
                <thead>
                  <tr className="border-b border-line">
                    <th className="text-left py-2 text-t3 text-sm font-medium">{t('analytics:financials.rateColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:financials.netColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:financials.vatColumn')}</th>
                    <th className="text-right py-2 text-t3 text-sm font-medium">{t('analytics:financials.grossColumn')}</th>
                  </tr>
                </thead>
                <tbody>
                  {vatRows.map((v, idx) => (
                    <tr key={`${v.rate ?? 'x'}-${idx}`} className="border-b border-line/50">
                      <td className="py-2 text-t1">{v.rate != null ? `${v.rate}%` : t('analytics:financials.rateUnknown')}</td>
                      <td className="text-right py-2 text-t2 tabular-nums">{formatCurrency(v.net)}</td>
                      <td className="text-right py-2 text-t2 tabular-nums">{formatCurrency(v.vat)}</td>
                      <td className="text-right py-2 text-t1 font-medium tabular-nums">{formatCurrency(v.gross)}</td>
                    </tr>
                  ))}
                </tbody>
                {vatRows.length > 1 && (
                  <tfoot>
                    <tr>
                      <td className="pt-2 text-t1 font-semibold">{t('analytics:financials.totalRow')}</td>
                      <td className="text-right pt-2 text-t1 font-semibold tabular-nums">{formatCurrency(vatTotal.net)}</td>
                      <td className="text-right pt-2 text-t1 font-semibold tabular-nums">{formatCurrency(vatTotal.vat)}</td>
                      <td className="text-right pt-2 text-t1 font-semibold tabular-nums">{formatCurrency(vatTotal.gross)}</td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
            <p className="text-xs text-t3 mt-3" data-testid="docs-vs-collections">
              {Math.abs(diff) < 0.005
                ? t('analytics:financials.docsMatch')
                : `${t('analytics:financials.docsVsCollections', { amount: signed(diff) })} — ${t('analytics:financials.docsVsCollectionsHint')}`}
            </p>
          </div>
        </div>
      )}

      {/* Revenue Trends */}
      <div className="bg-surface-2 dark:bg-surface-1 rounded-2xl border border-line overflow-hidden">
        <div className="p-5 border-b border-line">
          <h3 className="text-base font-semibold text-t1 flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            {t('analytics:financials.dailyRevenueTrend')}
          </h3>
        </div>
        <div className="p-5">
          {data.daily_revenue?.length > 0 ? (
            <>
              {(() => {
                const maxRevenue = Math.max(...data.daily_revenue.map((d: any) => d.revenue || 0), 1);
                const formatDateShort = (dateStr: string) => {
                  const date = new Date(dateStr);
                  return date.toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' });
                };
                return (
                  <>
                    <div className="flex">
                      <div className="flex flex-col justify-between h-44 pr-2 text-xs text-t4 w-14 shrink-0 text-right">
                        <span>{formatCurrency(maxRevenue)}</span>
                        <span>{formatCurrency(maxRevenue / 2)}</span>
                        <span>€0</span>
                      </div>
                      <div className="flex items-end gap-[3px] h-44 flex-1 border-l border-b border-line pl-1 pb-1">
                        {data.daily_revenue.map((day: any, idx: number) => {
                          const barHeight = maxRevenue > 0 ? ((day.revenue || 0) / maxRevenue) * 100 : 0;
                          return (
                            <div key={idx} className="flex-1 flex flex-col items-center relative group cursor-pointer"
                              onClick={(e) => {
                                const el = e.currentTarget.querySelector('[data-tooltip]') as HTMLElement;
                                if (el) el.classList.toggle('!opacity-100');
                              }}
                            >
                              <div className="relative w-full flex items-end justify-center h-full">
                                <div
                                  className="w-full max-w-[28px] mx-auto bg-blue-500 rounded-t-sm transition-all duration-300 group-hover:bg-blue-400"
                                  style={{ height: `${barHeight}%`, minHeight: day.revenue > 0 ? '4px' : '0' }}
                                />
                                <div data-tooltip className="absolute -top-10 left-1/2 -translate-x-1/2 bg-surface-0/95 text-t1 text-xs px-2.5 py-1.5 rounded-lg shadow-lg border border-line opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-20 pointer-events-none">
                                  <div className="font-semibold">{formatCurrency(day.revenue)}</div>
                                  <div className="text-t3">{formatDateShort(day.date)}</div>
                                </div>
                              </div>
                              <span className="text-[10px] text-t4 mt-1.5 leading-none">{formatDateShort(day.date)}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    {(() => {
                      const bestDay = data.daily_revenue.reduce((best: any, day: any) => (day.revenue || 0) > (best?.revenue || 0) ? day : best, data.daily_revenue[0]);
                      const totalRev = data.daily_revenue.reduce((sum: number, d: any) => sum + (d.revenue || 0), 0);
                      const avgPerDay = data.daily_revenue.length > 0 ? totalRev / data.daily_revenue.length : 0;
                      return (
                        <div className="flex items-center gap-4 mt-4 pt-3 border-t border-line/50 text-xs text-t3">
                          <div className="flex items-center gap-1.5">
                            <Zap className="w-3.5 h-3.5 text-blue-600 dark:text-blue-400" />
                            <span>{t('analytics:financials.topDay')} <span className="text-t1 font-medium">{formatDateShort(bestDay?.date)} ({formatCurrency(bestDay?.revenue)})</span></span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <BarChart2 className="w-3.5 h-3.5 text-t4" />
                            <span>{t('analytics:financials.avgPerDay')} <span className="text-t1 font-medium">{formatCurrency(avgPerDay)}</span></span>
                          </div>
                        </div>
                      );
                    })()}
                  </>
                );
              })()}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-t4">
              <div className="relative mb-4">
                <Calendar className="w-12 h-12 text-t4" />
                <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-surface-2 rounded-full flex items-center justify-center">
                  <Inbox className="w-3 h-3 text-t4" />
                </div>
              </div>
              <p className="font-medium text-t3">{t('analytics:financials.noRevenueData')}</p>
              <p className="text-xs text-t4 mt-1">{t('analytics:financials.dataWillAppearSales')}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalytics;
